import React, { useState, useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import tw from 'twin.macro';
import { useMutation } from '@apollo/client';
import { toast } from 'react-toastify';
import { SEND_PHONE_OTP, VERIFY_PHONE_OTP } from '../../services/authService/phoneMutations';
import { authActions } from '../../../store/authStore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencilAlt } from '@fortawesome/free-solid-svg-icons';
import { Colors } from '../../styles/colors';
import { isStagingTarget } from '../../../config/runtime-target';

const ModalOverlay = styled.div`
  ${tw`
    fixed
    inset-0
    bg-black
    bg-opacity-50
    flex
    items-center
    justify-center
    z-50
    p-4
  `}
`;

const ModalContent = styled.div`
  ${tw`
    py-10
    bg-white
    rounded-lg
    shadow-overlay
    w-full
    max-w-md
    p-6
    relative
    justify-between
    flex
    flex-col
  `}
  align-items: center;
`;

const Title = styled.h2`
  ${tw`
    text-2xl
    font-bold
    text-text-primary
    text-center
  `}
`;

const Description = styled.p`
  ${tw`
    text-center
    text-sm
    text-text-secondary
    mb-6

  `}
`;

const PhoneDisplay = styled.div`
  ${tw`
    flex
    justify-center
    mb-8
  `}
`;

const PhoneNumber = styled.span`
  ${tw`
    text-base
    font-medium
    text-text-primary
  `}
`;

const EditButton = styled.button`
  ${tw`
    text-black
    ml-2
    p-1
    border-none
    bg-transparent
  `}
  &:hover {
    ${tw`text-black`}
  }
`;

const Label = styled.label`
  ${tw`
    block
    text-sm
    font-medium
    text-text-secondary
    mb-1
  `}
`;

const InputGroup = styled.div`
  ${tw`
    flex
    items-center
    border
    border-border-default
    rounded-md
    overflow-hidden
    mb-6
    focus-within:ring-2
    focus-within:ring-gray-500
  `}
`;

const Prefix = styled.span`
  ${tw`
    bg-bg-secondary
    px-3
    py-2
    text-text-secondary
    border-r
    border-border-default
    font-medium
  `}
`;

const StyledInput = styled.input`
  ${tw`
    w-full
    px-3
    py-2
    focus:outline-none
  `}
`;

// OTP Input Boxes
const OtpContainer = styled.div`
  ${tw`
    flex
    justify-center
    gap-3
    mb-6
  `}
`;

const OtpInput = styled.input`
  ${tw`
    w-12
    h-14
    text-center
    text-xl
    font-semibold
    border-2
    rounded-lg
    focus:outline-none
    focus:border-black
    transition-colors
  `}
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  -moz-appearance: textfield;
`;

const ActionButton = styled.button<{ disabled?: boolean; secondary?: boolean }>`
  ${tw`
    mt-4
    w-full
    max-w-80
    py-3
    rounded-md
    font-semibold
    transition-colors
    duration-200
  `}
  ${({ disabled, secondary }) => [
    disabled
      ? tw`bg-gray-300 text-text-secondary cursor-not-allowed`
      : secondary
        ? tw`bg-white text-text-secondary border border-gray-400`
        : css`
            background: #0a0a0a;
            ${tw`text-white`}
          `,
  ]}
  ${({ disabled, secondary }) =>
    !disabled &&
    !secondary &&
    css`
      &:hover {
        background: #171717;
      }
    `}
  ${({ disabled, secondary }) =>
    !disabled &&
    secondary &&
    css`
      &:hover {
        ${tw`bg-bg-secondary`}
      }
    `}
`;

const ConsentBox = styled.div`
  ${tw`
    bg-bg-secondary
    border-l-4
    border-gray-400
    p-3
    rounded-md
    text-left
    w-full
    max-w-80
  `}
`;

const ConsentText = styled.p`
  ${tw`
    text-xs
    text-text-secondary
    leading-relaxed
  `}
`;

const ResendContainer = styled.div`
  ${tw`
    text-center
    mt-4
    p-3
    rounded-md
  `}
`;

const ResendText = styled.span`
  ${tw`
    text-sm
    text-text-secondary
  `}
`;

const ResendButton = styled.button<{ disabled?: boolean }>`
  ${tw`
    text-sm
    font-medium
    border-0
    bg-transparent
    ml-1
  `}
  ${({ disabled }) =>
    disabled
      ? tw`text-text-tertiary cursor-not-allowed`
      : css`
          color: ${Colors.PrimaryBlueDark};
        `}
  ${({ disabled }) =>
    !disabled &&
    css`
      &:hover {
        color: ${Colors.PrimaryIndigo};
      }
    `}
`;

const TimerText = styled.span`
  ${tw`
    text-sm
    text-text-tertiary
  `}
`;

const CloseButton = styled.button`
  ${tw`
    absolute
    top-4
    right-4
    text-text-tertiary
    w-6
    h-6
    flex
    border-0
    bg-transparent
  `}
  &:hover {
    ${tw`text-text-secondary`}
  }
`;

interface PhoneVerificationProps {
  onClose: () => void;
  onVerified?: () => void;
  initialPhoneNumber?: string;
}

export const PhoneVerification: React.FC<PhoneVerificationProps> = ({
  onClose,
  onVerified,
  initialPhoneNumber = '',
}) => {
  const [step, setStep] = useState<'phone' | 'otp'>('phone');
  const [phoneNumber, setPhoneNumber] = useState(initialPhoneNumber.replace('+1', ''));
  const [otpDigits, setOtpDigits] = useState<string[]>(['', '', '', '', '']);
  const [timer, setTimer] = useState(0);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const otpInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const [sendOtp, { loading: sendingOtp }] = useMutation(SEND_PHONE_OTP);
  const [verifyOtp, { loading: verifyingOtp }] = useMutation(VERIFY_PHONE_OTP);

  // Get full OTP code from digits
  const otpCode = otpDigits.join('');

  useEffect(() => {
    if (timer > 0) {
      timerRef.current = setTimeout(() => setTimer(timer - 1), 1000);
    }
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [timer]);

  const validatePhone = (phone: string) => /^\d{10}$/.test(phone.replace(/\D/g, ''));

  // Handle individual OTP digit input
  const handleOtpChange = (index: number, value: string) => {
    // Only allow single digit
    if (value.length > 1) {
      value = value.slice(-1);
    }

    // Only allow numbers
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newDigits = [...otpDigits];
    newDigits[index] = value;
    setOtpDigits(newDigits);

    // Auto-focus next input
    if (value && index < 4) {
      otpInputRefs.current[index + 1]?.focus();
    }
  };

  // Handle backspace to go to previous input
  const handleOtpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  // Handle paste for OTP
  const handleOtpPaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 5);
    if (pastedData) {
      const newDigits = [...otpDigits];
      for (let i = 0; i < pastedData.length && i < 5; i++) {
        newDigits[i] = pastedData[i];
      }
      setOtpDigits(newDigits);
      // Focus the next empty input or the last one
      const nextEmptyIndex = newDigits.findIndex(d => !d);
      const focusIndex = nextEmptyIndex === -1 ? 4 : nextEmptyIndex;
      otpInputRefs.current[focusIndex]?.focus();
    }
  };

  // Format timer as MM:SS
  const formatTimer = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSendOtp = async () => {
    const formattedPhone = `+1${phoneNumber.replace(/\D/g, '')}`;
    if (!validatePhone(phoneNumber)) {
      toast.error('Please enter a valid 10-digit US number.');
      return;
    }

    try {
      const { data } = await sendOtp({ variables: { phoneNumber: formattedPhone } });
      if (data?.sendPhoneOtp) {
        setStep('otp');
        setTimer(60);
        setOtpDigits(['', '', '', '', '']); // Reset OTP digits
        toast.info('Verification code sent.');
        // Focus first OTP input after transition
        setTimeout(() => otpInputRefs.current[0]?.focus(), 100);
      }
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const handleVerifyOtp = async () => {
    const formattedPhone = `+1${phoneNumber.replace(/\D/g, '')}`;
    if (otpCode.length !== 5) {
      toast.error('Please enter the 5-digit code.');
      return;
    }

    try {
      const { data } = await verifyOtp({
        variables: { phoneNumber: formattedPhone, code: otpCode },
      });
      if (data?.verifyPhoneOtp) {
        authActions.setUser(data.verifyPhoneOtp);
        toast.success('Phone number verified successfully!');
        onClose();
        // Trigger callback after successful verification (e.g., to continue with payment)
        onVerified?.();
      }
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <CloseButton onClick={onClose}>
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </CloseButton>

        {step === 'phone' ? (
          <>
            <Title>Phone Verification</Title>
            <Description>Enter your US phone number to receive a verification code.</Description>

            <Label>Phone Number</Label>
            <InputGroup>
              <Prefix>+1</Prefix>
              <StyledInput
                type="tel"
                placeholder="555-555-5555"
                value={phoneNumber}
                onChange={e => setPhoneNumber(e.target.value)}
                maxLength={12}
                autoFocus
              />
            </InputGroup>
            <ConsentBox>
              <ConsentText>
                By clicking "Send Verification Code", you consent to receive a one-time verification
                code via SMS. Message and data rates may apply. This is a transactional message for
                account verification purposes only.
              </ConsentText>
            </ConsentBox>
            <ActionButton
              onClick={handleSendOtp}
              disabled={sendingOtp || !validatePhone(phoneNumber)}
            >
              {sendingOtp ? 'Sending...' : 'Send Verification Code'}
            </ActionButton>
          </>
        ) : (
          <>
            <Title>We just sent an SMS</Title>
            <Description>Enter the security code we sent to</Description>
            {isStagingTarget() && (
              <Description tw="text-amber-700 font-medium">
                Staging: use verification code 12345.
              </Description>
            )}

            <PhoneDisplay>
              <PhoneNumber>
                +1 {phoneNumber.slice(0, 3)} {phoneNumber.slice(3, 6)} {phoneNumber.slice(6)}
              </PhoneNumber>
              <EditButton onClick={() => setStep('phone')} disabled={verifyingOtp}>
                <FontAwesomeIcon icon={faPencilAlt} size="sm" />
              </EditButton>
            </PhoneDisplay>

            <OtpContainer onPaste={handleOtpPaste}>
              {otpDigits.map((digit, index) => (
                <OtpInput
                  key={index}
                  ref={el => (otpInputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={e => handleOtpChange(index, e.target.value)}
                  onKeyDown={e => handleOtpKeyDown(index, e)}
                  disabled={verifyingOtp}
                />
              ))}
            </OtpContainer>

            <ActionButton onClick={handleVerifyOtp} disabled={verifyingOtp || otpCode.length !== 5}>
              {verifyingOtp ? 'Verifying...' : 'Verify'}
            </ActionButton>

            <ResendContainer>
              <ResendText>Didn't receive code? </ResendText>
              {timer > 0 ? (
                <>
                  <ResendButton disabled>Resend</ResendButton>
                  <TimerText> - {formatTimer(timer)}</TimerText>
                </>
              ) : (
                <ResendButton onClick={handleSendOtp} disabled={sendingOtp}>
                  {sendingOtp ? 'Sending...' : 'Resend'}
                </ResendButton>
              )}
            </ResendContainer>
          </>
        )}
      </ModalContent>
    </ModalOverlay>
  );
};
